import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './QuizResultsPanel.css';

/**
 * 퀴즈 결과 패널 컴포넌트
 * - 실시간 리더보드
 * - 플레이어별 상세 결과
 * - 통계 요약
 */
const QuizResultsPanel = ({ roomCode, isOpen, onClose, socket }) => {
  const [activeTab, setActiveTab] = useState('leaderboard'); // 'leaderboard', 'results', 'stats'
  const [leaderboard, setLeaderboard] = useState([]);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 웹소켓 이벤트 리스너 등록
  useEffect(() => {
    if (!isOpen || !roomCode || !socket) return;

    // 초기 데이터 로드
    fetchData();

    // 실시간 이벤트 리스너 (서버에서 보내는 이벤트와 일치)
    const handleAnswerResult = (data) => {
      console.log('📨 answer:result 수신:', data);
      fetchData(); // 모든 데이터 다시 로드
    };

    const handleScoreUpdate = (data) => {
      console.log('📨 score:update 수신:', data);

      // ✅ DB 조회 대신 실시간 데이터로 리더보드 업데이트
      if (data && data.scores && activeTab === 'leaderboard') {
        const updatedLeaderboard = Object.entries(data.scores).map(([playerId, score]) => ({
          player_id: playerId,
          player_name: data.trigger?.playerName || 'Unknown',
          total_score: score,
          accuracy_rate: 0, // 리더보드만 업데이트하므로 정답률은 유지
          avg_time_spent: 0
        })).sort((a, b) => b.total_score - a.total_score);

        setLeaderboard(prev => {
          // 기존 플레이어 정보 유지하면서 점수만 업데이트
          return updatedLeaderboard.map(newPlayer => {
            const existingPlayer = prev.find(p => p.player_id === newPlayer.player_id);
            return existingPlayer ? { ...existingPlayer, total_score: newPlayer.total_score } : newPlayer;
          });
        });
      } else {
        // 리더보드가 아닌 탭에서는 DB 조회
        fetchData();
      }
    };

    const handleRewardItem = (data) => {
      console.log('📨 reward:item 수신:', data);
      // 보상 아이템은 결과에 포함되므로 fetchData로 갱신
      if (activeTab === 'results' || activeTab === 'stats') {
        fetchData();
      }
    };

    socket.on('answer:result', handleAnswerResult);
    socket.on('score:update', handleScoreUpdate);
    socket.on('reward:item', handleRewardItem);

    return () => {
      socket.off('answer:result', handleAnswerResult);
      socket.off('score:update', handleScoreUpdate);
      socket.off('reward:item', handleRewardItem);
    };
  }, [roomCode, isOpen, activeTab, socket]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      switch (activeTab) {
        case 'leaderboard':
          await fetchLeaderboard();
          break;
        case 'results':
          await fetchResults();
          break;
        case 'stats':
          await fetchStats();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Failed to fetch quiz data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    const response = await axios.get(`/api/quiz-results/room/${roomCode}/leaderboard`);
    setLeaderboard(response.data.leaderboard || []);
  };

  const fetchResults = async () => {
    const response = await axios.get(`/api/quiz-results/room/${roomCode}`);
    setResults(response.data.results || []);
  };

  const fetchStats = async () => {
    const response = await axios.get(`/api/quiz-results/room/${roomCode}/stats`);
    setStats(response.data.stats || null);
  };

  const getRarityColor = (rarity) => {
    const colors = {
      legendary: '#FFD700', // 금색
      epic: '#A335EE',      // 보라색
      rare: '#0070DD',      // 파란색
      common: '#9D9D9D'     // 회색
    };
    return colors[rarity] || '#FFFFFF';
  };

  const getRarityEmoji = (rarity) => {
    const emojis = {
      legendary: '🌟',
      epic: '💎',
      rare: '💠',
      common: '📦'
    };
    return emojis[rarity] || '❓';
  };

  return (
    <div className="quiz-results-panel">
      {/* 헤더 */}
      <div className="panel-header">
        <h2>🏆 실시간 점수</h2>
      </div>

        {/* 탭 메뉴 */}
        <div className="panel-tabs">
          <button
            className={`tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('leaderboard')}
          >
            🏅 순위표
          </button>
          <button
            className={`tab ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setActiveTab('results')}
          >
            📋 상세 결과
          </button>
          <button
            className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            📊 통계
          </button>
        </div>

        {/* 내용 */}
        <div className="panel-content">
          {loading && <div className="loading">로딩 중...</div>}
          {error && <div className="error">오류: {error}</div>}

          {!loading && !error && activeTab === 'leaderboard' && (
            <div className="leaderboard">
              {leaderboard.length === 0 ? (
                <p className="empty">아직 결과가 없습니다.</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>순위</th>
                      <th>플레이어</th>
                      <th>점수</th>
                      <th>정답률</th>
                      <th>평균 시간</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((player, index) => (
                      <tr key={player.player_id} className={index < 3 ? `rank-${index + 1}` : ''}>
                        <td className="rank">
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                          {index > 2 && (index + 1)}
                        </td>
                        <td className="player-name">{player.player_name}</td>
                        <td className="score">{player.total_score}</td>
                        <td className="accuracy">{player.accuracy_rate}%</td>
                        <td className="time">{player.avg_time_spent}초</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {!loading && !error && activeTab === 'results' && (
            <div className="results-list">
              {results.length === 0 ? (
                <p className="empty">아직 결과가 없습니다.</p>
              ) : (
                results.map((result) => (
                  <div key={result.id} className={`result-item ${result.is_correct ? 'correct' : 'wrong'}`}>
                    <div className="result-header">
                      <span className="player-name">{result.player_name}</span>
                      <span className="result-icon">{result.is_correct ? '✅' : '❌'}</span>
                      <span className={`score ${result.score_delta < 0 ? 'negative' : 'positive'}`}>
                        {result.score_delta > 0 ? '+' : ''}{result.score_delta}
                      </span>
                    </div>
                    <div className="result-details">
                      <p className="question">Q{result.question_id}: {result.question_title}</p>
                      <p className="time">⏱️ {result.time_spent}초</p>
                      {result.reward_item && (
                        <div className="reward" style={{ color: getRarityColor(result.reward_item.rarity) }}>
                          {getRarityEmoji(result.reward_item.rarity)} {result.reward_item.name}
                        </div>
                      )}
                    </div>
                    <div className="timestamp">
                      {new Date(result.created_at).toLocaleString('ko-KR')}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {!loading && !error && activeTab === 'stats' && stats && (
            <div className="stats-panel">
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>👥 참가자</h3>
                  <p className="stat-value">{stats.total_players}명</p>
                </div>
                <div className="stat-card">
                  <h3>📝 총 답변</h3>
                  <p className="stat-value">{stats.total_answers}개</p>
                </div>
                <div className="stat-card">
                  <h3>✅ 정답</h3>
                  <p className="stat-value">{stats.correct_answers}개</p>
                </div>
                <div className="stat-card">
                  <h3>❌ 오답</h3>
                  <p className="stat-value">{stats.wrong_answers}개</p>
                </div>
                <div className="stat-card">
                  <h3>📊 정답률</h3>
                  <p className="stat-value">{stats.accuracy_rate}%</p>
                </div>
                <div className="stat-card">
                  <h3>⏱️ 평균 시간</h3>
                  <p className="stat-value">{stats.avg_time_spent}초</p>
                </div>
              </div>

              <div className="reward-stats">
                <h3>🎁 보상 아이템 통계</h3>
                <div className="reward-grid">
                  <div className="reward-stat" style={{ color: getRarityColor('legendary') }}>
                    <span>{getRarityEmoji('legendary')} Legendary</span>
                    <span className="count">{stats.legendary_count}개</span>
                  </div>
                  <div className="reward-stat" style={{ color: getRarityColor('epic') }}>
                    <span>{getRarityEmoji('epic')} Epic</span>
                    <span className="count">{stats.epic_count}개</span>
                  </div>
                  <div className="reward-stat" style={{ color: getRarityColor('rare') }}>
                    <span>{getRarityEmoji('rare')} Rare</span>
                    <span className="count">{stats.rare_count}개</span>
                  </div>
                  <div className="reward-stat" style={{ color: getRarityColor('common') }}>
                    <span>{getRarityEmoji('common')} Common</span>
                    <span className="count">{stats.common_count}개</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      {/* 하단 버튼 */}
      <div className="panel-footer">
        <button className="refresh-button" onClick={fetchData} disabled={loading}>
          🔄 새로고침
        </button>
      </div>
    </div>
  );
};

export default QuizResultsPanel;
